import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FilledInput,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Typography,
} from "@material-ui/core";
import { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "react-toastify/dist/ReactToastify.css";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import SaveIcon from "@material-ui/icons/Save";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddKSADialog from "../../components/addKSADialog";
import fnOpenToast from "../../components/toast";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import { Link as ReactLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import exportFromJSON from "export-from-json";
import { notifyError } from "../../utils/toast";
import {
    getInvoiceResourceGeneration,
    createInvoice,
    createCreditNote,
    getCountriesCurrencies,
} from "../../api";
import { useOktaAuth } from "@okta/okta-react";

const useStyles = makeStyles((theme) => ({
    formControl: {
        marginRight: theme.spacing(1),
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
}));

LicenseManager.setLicenseKey(
    "Using_this_{AG_Grid}_Enterprise_key_{AG-062453}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Lucid_USA,_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{V360}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{V360}_need_to_be_licensed___{V360}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_August_2025}____[v3]_[01]_MTc1NjMzNTYwMDAwMA==83cd4edac4587e17c3187cf63f89d914",
);

const KsaDefaultFilterStateKey = "ksaDefaultFilterState";
const KsaColumnMovedStateKey = "ksaColumnMovedStateKey";

const legalEntityMap = new Map();
legalEntityMap.set("EU", "Lucid Europe BV - EU");
legalEntityMap.set("GF", "Lucid Global Forward BV - GF");
legalEntityMap.set("NL", "Lucid Netherlands BV - NL");
legalEntityMap.set("DE", "Lucid Germany GmbH - DE");
legalEntityMap.set("CH", "Lucid Switzerland AG - CH");
legalEntityMap.set("NO", "Lucid Norway AS - NO");
legalEntityMap.set("BE", "Lucid Belgium BV - BE");
legalEntityMap.set("SA", "Lucid KSA - SA");
legalEntityMap.set("UA", "Lucid United Arab Emirates - UA");

const purchaseTypeMap = new Map();
purchaseTypeMap.set("V", "Vehicle Sales");
purchaseTypeMap.set("I", "Inter Company");
purchaseTypeMap.set("R", "Service Repair");
purchaseTypeMap.set("P", "Parts");
purchaseTypeMap.set("M", "Merchandise");
purchaseTypeMap.set("S", "Sub-Lease");
purchaseTypeMap.set("C", "Commission");
purchaseTypeMap.set("D", "Transportation Damage");
purchaseTypeMap.set("O", "Other");

const ksaLegalEntityMap = new Map();
ksaLegalEntityMap.set("4000", "Lucid LLC");
ksaLegalEntityMap.set("4001", "Lucid LLC Manufacturing");
ksaLegalEntityMap.set("4010", "Lucid LLC Trading Branch");

const customerTypeMap = new Map();
customerTypeMap.set("B", "B2B");
customerTypeMap.set("C", "B2C");
customerTypeMap.set("G", "B2G");
customerTypeMap.set("I", "I/C");

const referenceTypeMap = new Map();
referenceTypeMap.set("Order", "Order");
referenceTypeMap.set("Reservation", "Reservation");
referenceTypeMap.set("Other", "Other");
referenceTypeMap.set("Credit Note", "Credit Note");
referenceTypeMap.set("Service Invoice", "Service Invoice");
referenceTypeMap.set("Service Authorization", "Service Authorization");
referenceTypeMap.set("Service Sale", "Service Sale");
referenceTypeMap.set("WorkOrder", "WorkOrder");

const statusTypeMap = new Map();
statusTypeMap.set("Created", "Created");
statusTypeMap.set("Cancelled", "Cancelled");

const initialValue = {
    legalEntity: { value: "", hasError: false },
    currency: { value: "", hasError: false },
    referenceNumber: { value: "", hasError: false },
    referenceType: { value: "", hasError: false },
    purchaseType: { value: "", hasError: false },
    customerType: { value: "", hasError: false },
    status: { value: "", hasError: false },
    ksaLegalEntity: { value: "", hasError: false },
    description: { value: "", hasError: false },
};

const initialColumn = [
    {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        pinned: "left",
        lockPosition: true,
        suppressMenu: true,
        suppressSizeToFit: true,
        field: "checkboxBtn",
        filter: false,
        sortable: false,
    },
    {
        headerName: "Invoice Number",
        field: "invoiceNumber",
        filter: "agTextColumnFilter",
        minWidth: 180,
    },
    {
        headerName: "Legal Entity",
        field: "legalEntity",
        filter: "agSetColumnFilter",
        filterParams: {
            values: [...legalEntityMap.keys()],
        },
        sortable: true,
    },
    {
        headerName: "SAP Company Code",
        field: "sapCompanyCode",
        filter: "agTextColumnFilter",
        minWidth: 180,
    },
    {
        headerName: "Currency",
        field: "currency",
        filter: "agTextColumnFilter",
        sortable: true,
    },
    {
        headerName: "Reference Number",
        field: "referenceNumber",
        filter: "agTextColumnFilter",
        sortable: true,
    },
    {
        headerName: "Reference Type",
        field: "referenceType",
        filter: "agSetColumnFilter",
        filterParams: {
            values: [...referenceTypeMap.keys()],
        },
        sortable: true,
    },
    {
        headerName: "Purchase Type",
        field: "purchaseType",
        valueFormatter: (params) => {
            if (params.data) {
                return purchaseTypeMap.get(params.data.purchaseType);
            }
            return "";
        },
        filter: "agSetColumnFilter",
        filterParams: {
            valueFormatter: (params) => {
                return purchaseTypeMap.get(params.value);
            },
            values: [...purchaseTypeMap.keys()],
        },
        sortable: true,
    },
    {
        headerName: "Customer Type",
        field: "customerType",
        valueFormatter: (params) => {
            if (params.data) {
                return customerTypeMap.get(params.data.customerType);
            }
            return "";
        },
        filter: "agSetColumnFilter",
        filterParams: {
            valueFormatter: (params) => {
                return customerTypeMap.get(params.value);
            },
            values: [...customerTypeMap.keys()],
        },
        sortable: true,
    },
    {
        headerName: "Status",
        field: "status",
        filter: "agSetColumnFilter",
        filterParams: {
            values: [...statusTypeMap.keys()],
        },
        sortable: true,
    },
    {
        headerName: "Generated By",
        field: "generatedByName",
        filter: "agTextColumnFilter",
        sortable: true,
    },
    {
        headerName: "Generated On",
        field: "generatedDate",
        filter: "agDateColumnFilter",
        sortable: true,
    },
    {
        headerName: "Description",
        field: "description",
        valueFormatter: (params) => {
            return params.value || "";
        },
        filter: "agTextColumnFilter",
        sortable: true,
    },
];

function KSA() {
    const gridRef = useRef();
    const [open, setOpen] = useState(false);
    const [countries, setCountries] = useState(new Map());
    const [gridApi, setGridApi] = useState(null);
    const [formData, setFormData] = useState(initialValue);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [currInvoiceNumber, setCurrInvoiceNumber] = useState("");
    const [openConfirmModal, setConfirmModal] = useState(false);
    const [perPage, setPerPage] = useState(25);
    const [isCCNoteCreated, setIsCCNoteCreated] = useState(false);
    const [openConfirmCCNoteModal, setOpenConfirmCCNoteModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [columnDefs, setColumnDefs] = useState(initialColumn);
    const [clearButtonDisplay, setClearButtonDisplay] = useState(false);
    const [defaultChoice, setDefaultChoice] = useState([]);
    const classes = useStyles();

    const { authState } = useOktaAuth();

    const onBtnSetDefaultFilter = (params) => {
        if (gridRef.current) {
            const filterState = gridRef.current.api.getFilterModel();
            const columnMovedState = gridRef.current.api.getColumnState();

            if (filterState) {
                localStorage.setItem(
                    KsaDefaultFilterStateKey,
                    JSON.stringify(filterState),
                );
            } else {
                localStorage.removeItem(KsaDefaultFilterStateKey);
            }

            if (columnMovedState) {
                localStorage.setItem(
                    KsaColumnMovedStateKey,
                    JSON.stringify(columnMovedState),
                );
            } else {
                localStorage.removeItem(KsaColumnMovedStateKey);
            }
        }

        fnOpenToast("Adjusted default filters", "success");
    };

    const onBtnClearDefaultFilter = () => {
        if (gridRef.current) {
            gridRef.current.api.setFilterModel(null);
            const initialState = initialColumn.map((col) => ({
                colId: col.field,
                sort: null,
            }));
            gridRef.current.api.applyColumnState({
                state: initialState,
                applyOrder: true,
            });

            localStorage.removeItem(KsaDefaultFilterStateKey);
            localStorage.removeItem(KsaColumnMovedStateKey);

            fnOpenToast("Cleared default filters", "success");
        }
    };

    const onBtnClearFilter = () => {
        if (gridRef.current) {
            gridRef.current.api.setFilterModel(null);

            const initialState = initialColumn.map((col) => ({
                colId: col.field,
                sort: null,
            }));
            gridRef.current.api.applyColumnState({
                state: initialState,
                applyOrder: true,
            });

            fnOpenToast("Cleared filters", "success");
        }
    };

    const defaultColDef = {
        sortable: true,
        filter: true,
        floatingFilter: true,
        editable: false,
        resizable: true,
    };

    const onGridReady = (params) => {
        const savedFilterState = localStorage.getItem(KsaDefaultFilterStateKey);
        const savedColumnMovedState = localStorage.getItem(
            KsaColumnMovedStateKey,
        );

        if (savedFilterState) {
            const parsedState = JSON.parse(savedFilterState);
            params.api.setFilterModel(parsedState);
        }

        if (savedColumnMovedState) {
            const parsedState = JSON.parse(savedColumnMovedState);
            params.columnApi.applyColumnState({
                state: parsedState,
                applyOrder: true,
            });
        }

        setGridApi(params.api);
    };

    useEffect(() => {
        if (!(gridApi || currInvoiceNumber || isCCNoteCreated)) {
            return;
        }

        const dataSource = {
            getRows: async (params) => {
                const page = Math.floor(params.request.startRow / perPage) + 1;
                const payload = {
                    pageNumber: page,
                    pageLimit: perPage,
                };
                if (params.request.filterModel) {
                    payload["filterModel"] = params.request.filterModel;
                    if (Object.keys(params.request.filterModel).length > 0) {
                        setClearButtonDisplay(true);
                    } else {
                        setClearButtonDisplay(false);
                    }
                }
                if (params.request.sortModel) {
                    payload["sortModel"] = params.request.sortModel;
                }

                try {
                    const { data, count } =
                        await getInvoiceResourceGeneration(payload);
                    const lastRow =
                        count <= params.request.endRow ? count : undefined;
                    params.success({
                        rowData: data,
                        rowCount: lastRow,
                    });
                } catch (error) {
                    params.fail();
                }
                localStorage.setItem(
                    "filterDataSource",
                    JSON.stringify(payload.filterModel),
                );
            },
        };
        gridApi.setGridOption("serverSideDatasource", dataSource);
    }, [gridApi, currInvoiceNumber, perPage, isCCNoteCreated]);

    const getCountries = async () => {
        const countriesCurrencies = await getCountriesCurrencies(true);
        countriesCurrencies.map((item) => {
            countries.set(item.countryISO2Code, item);
        });
        setCountries(countries);
    };

    useEffect(() => {
        getCountries();
    }, []);

    const onChange = (e) => {
        const { value, id, name } = e.target;
        setFormData({
            ...formData,
            [id ?? name]: { value: value, hasError: false },
        });
    };

    const handleDefaultChoice = (value) => {
        setDefaultChoice(value);
        localStorage.setItem(
            "defaultFiltersForNewInvoiceGeneration",
            JSON.stringify(formData),
        );
        localStorage.setItem("CurrencyForLegalEntity", formData.currency.value);
        fnOpenToast("Filters set as default", "success");
    };

    const handleClearFilters = () => {
        localStorage.removeItem("defaultFiltersForNewInvoiceGeneration");
        setFormData(initialValue);
        fnOpenToast("Filters cleared", "success");
    };

    const handlePerPageChange = (e) => {
        setPerPage(e.target.value);
    };

    const handleClickOpen = useCallback(() => {
        setOpen(true);
        if (localStorage.getItem("defaultFiltersForNewInvoiceGeneration")) {
            setFormData(
                JSON.parse(
                    localStorage.getItem(
                        "defaultFiltersForNewInvoiceGeneration",
                    ),
                ),
            );
        }
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleActionClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleActionClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const onFirstDataRendered = useCallback(() => {
        gridRef.current.api.sizeColumnsToFit();
    }, []);

    const handleFormSubmit = (event) => {
        event.preventDefault();
        setConfirmModal(true);
    };

    const constructRequest = (formData) => {
        const request = {
            legalEntity: formData.legalEntity.value,
            currency: formData.currency.value,
            referenceNumber: formData.referenceNumber.value,
            referenceType: formData.referenceType.value,
            purchaseType: formData.purchaseType.value,
            customerType: formData.customerType.value,
            generatedById: authState?.accessToken?.claims?.sub,
            generatedByName: authState?.idToken?.claims?.name,
            status: formData.status.value,
            ksaLegalEntity: formData.ksaLegalEntity.value,
            description: formData.description.value || null,
        };
        return request;
    };

    const handleSuccessModalClose = useCallback(() => {
        setOpenSuccessModal(false);
    });

    const handleConfirmModalClose = useCallback(() => {
        setConfirmModal(false);
    });

    const handleGenerate = useCallback(async () => {
        setConfirmModal(false);
        const request = constructRequest(formData);
        try {
            const { invoiceNumber } = await createInvoice(request);
            if (invoiceNumber) {
                setCurrInvoiceNumber(invoiceNumber);
                setOpenSuccessModal(true);
                handleClose();
                gridRef.current.api.refreshServerSide({ purge: true });
                setFormData(initialValue);
                fnOpenToast("Invoice Number Generated Successfully", "success");
                handleDefaultChoice();
            }
        } catch (error) {
            notifyError(error.message);
        }
    });

    const handleCreateCreditNoteAction = useCallback(() => {
        const rows = gridRef.current.api.getSelectedRows();
        setSelectedRows(rows);
        if (rows.length === 0) {
            fnOpenToast(
                "Select invoice number records to create credit note !!!",
                "error",
            );
            return;
        } else if (
            rows[0]?.referenceType !== "Credit Note" &&
            rows[0]?.status === "Created"
        ) {
            setOpenConfirmCCNoteModal(true);
        } else {
            fnOpenToast(
                "You cannot generate a credit note for a cancelled invoice number or credit note",
                "error",
            );
        }
    }, []);

    const handleAcceptCCNoteGeneration = useCallback(async () => {
        const rows = gridRef.current.api.getSelectedRows();
        rows.map((item) => {
            item["generatedById"] = authState?.accessToken?.claims?.sub;
            item["generatedByName"] = authState?.idToken?.claims?.name;
            return item;
        });
        const ccNoteRequest = {
            createCreditNote: rows,
        };

        setIsCCNoteCreated(false);
        await createCreditNote(ccNoteRequest);
        setIsCCNoteCreated(true);
        setOpenConfirmCCNoteModal(false);
    }, []);

    const handleCancelCCNoteGeneration = useCallback(() => {
        setOpenConfirmCCNoteModal(false);
    }, []);

    const getRowId = useMemo(() => {
        return (params) => {
            return params.data._id;
        };
    }, []);

    const refreshInvoiceResourceGeneration = async (extension) => {
        const payload = {};
        payload["filterModel"] = JSON.parse(
            localStorage.getItem("filterDataSource"),
        );
        payload["pageLimit"] = 4000;

        const { data } = await getInvoiceResourceGeneration(payload);
        const fileName = "Sequential Invoice Numbers";
        const exportType = exportFromJSON.types[extension];
        exportFromJSON({ data, fileName, exportType });
    };

    const onBtExport = useCallback(async () => {
        await refreshInvoiceResourceGeneration("xls");
    }, []);

    const onBtnCSVExport = useCallback(async () => {
        await refreshInvoiceResourceGeneration("csv");
    }, []);

    return (
        <>
            <div>
                <div style={{ margin: "10px 20px" }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <ReactLink
                            color="inherit"
                            to="/"
                            className="breadcrumb-link"
                        >
                            Dashboard
                        </ReactLink>
                        <Typography color="textPrimary">
                            Invoice Numbers
                        </Typography>
                    </Breadcrumbs>
                </div>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    style={{ margin: "10px 20px" }}
                >
                    <div>
                        <Typography
                            variant="h5"
                            component={"h5"}
                            style={{
                                fontFamily: "GTAmericaMedium",
                                fontSize: "24px",
                                lineHeight: "120%",
                                fontWeight: "500",
                            }}
                        >
                            Sequential Invoice Numbers
                        </Typography>
                    </div>
                    <Box display="flex" justifyContent="space-between">
                        <div>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <InputLabel id="per-page-select-label">
                                    Per Page
                                </InputLabel>
                                <Select
                                    labelId="per-page-select-label"
                                    id="perPage-select"
                                    value={perPage}
                                    onChange={handlePerPageChange}
                                    label="Per Page"
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={15}>15</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                    <MenuItem value={4000}>4000</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        <Button
                            variant="outlined"
                            color="primary"
                            aria-controls="action-menu"
                            aria-haspopup="true"
                            onClick={handleActionClick}
                        >
                            Actions <ExpandMoreIcon />
                        </Button>
                        <Menu
                            id="action-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleActionClose}
                        >
                            <MenuItem onClick={handleClickOpen}>
                                <AddCircleOutlineIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                New Invoice Number
                            </MenuItem>
                            <MenuItem onClick={handleCreateCreditNoteAction}>
                                <AddCircleOutlineIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />
                                Create Credit Note
                            </MenuItem>
                            <MenuItem onClick={onBtExport}>
                                <SaveAltIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Export to Excel
                            </MenuItem>
                            <MenuItem onClick={onBtnCSVExport}>
                                <SaveAltIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Export to CSV
                            </MenuItem>
                            <MenuItem onClick={onBtnSetDefaultFilter}>
                                <SaveIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Set default filters
                            </MenuItem>
                            <MenuItem onClick={onBtnClearDefaultFilter}>
                                <ClearIcon
                                    fontSize="small"
                                    style={{ marginRight: "5px" }}
                                />{" "}
                                Clear default filters
                            </MenuItem>
                            {clearButtonDisplay && (
                                <MenuItem onClick={onBtnClearFilter}>
                                    <ClearIcon
                                        fontSize="small"
                                        style={{ marginRight: "5px" }}
                                    />{" "}
                                    Clear filters
                                </MenuItem>
                            )}
                        </Menu>
                    </Box>
                </Box>

                <table
                    style={{
                        margin: "0 20px",
                        width: "calc(100% - 40px)",
                        height: "calc(100vh - 175px)",
                    }}
                >
                    <tbody>
                        <tr>
                            <td style={{ border: "1px solid #babfc7" }}>
                                <div
                                    className="ag-theme-alpine"
                                    style={{
                                        height: "100%",
                                        width: "100%",
                                    }}
                                >
                                    <AgGridReact
                                        ref={gridRef}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        onFirstDataRendered={
                                            onFirstDataRendered
                                        }
                                        pagination={true}
                                        rowModelType={"serverSide"}
                                        onGridReady={onGridReady}
                                        paginationPageSize={perPage}
                                        cacheBlockSize={perPage}
                                        getRowId={getRowId}
                                        paginationPageSizeSelector={[
                                            10, 15, 25, 50, 100, 4000,
                                        ]}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <AddKSADialog
                    open={open}
                    handleClose={handleClose}
                    data={formData}
                    onChange={onChange}
                    handleFormSubmit={handleFormSubmit}
                    countries={countries}
                    legalEntityMap={legalEntityMap}
                    purchaseTypeMap={purchaseTypeMap}
                    customerTypeMap={customerTypeMap}
                    referenceTypeMap={referenceTypeMap}
                    ksaLegalEntityMap={ksaLegalEntityMap}
                    handleDefaultChoice={handleDefaultChoice}
                    handleClearFilters={handleClearFilters}
                />
                <Dialog open={openSuccessModal} fullWidth={true} maxWidth="sm">
                    <DialogTitle id="alert-dialog-title">
                        Generate a New Invoice Number
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" component="p">
                            Assigned Invoice Number:
                        </Typography>
                        <div
                            style={{ marginBottom: "15px", display: "block" }}
                        ></div>

                        <FormControl variant="filled" fullWidth>
                            <InputLabel htmlFor="copy-generated-invoice-number"></InputLabel>
                            <FilledInput
                                id="copy-generated-invoice-number"
                                type="text"
                                value={currInvoiceNumber}
                                disabled
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="copy generated invoice number"
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    currInvoiceNumber,
                                                );
                                            }}
                                        >
                                            <FileCopyOutlinedIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            onClick={handleSuccessModalClose}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openConfirmModal} fullWidth={true} maxWidth="sm">
                    <DialogContent style={{ textAlign: "center" }}>
                        <Typography variant="h4" component="h4">
                            Are you sure?
                        </Typography>
                        <div
                            style={{ marginBottom: "15px", display: "block" }}
                        ></div>
                        <Typography variant="body1" component="p">
                            You're about to generate invoice number for the
                            order number{" "}
                            <strong>{formData.referenceNumber.value}</strong>
                        </Typography>
                        <Typography
                            variant="body1"
                            component="p"
                            color="secondary"
                        >
                            Please note, this action is not reversible.
                        </Typography>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            type="submit"
                            color="secondary"
                            variant="contained"
                            onClick={handleConfirmModalClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            onClick={handleGenerate}
                        >
                            Generate now
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={openConfirmCCNoteModal}
                    fullWidth={true}
                    maxWidth="sm"
                >
                    <DialogContent style={{ textAlign: "center" }}>
                        <Typography variant="h4" component="h4">
                            Are you sure?
                        </Typography>
                        <div
                            style={{ marginBottom: "15px", display: "block" }}
                        ></div>
                        <Typography variant="paragraph" component="p">
                            Are you sure, you want to create a credit note for
                            this invoice number?
                        </Typography>
                        {selectedRows.length > 0 && (
                            <ul>
                                {selectedRows.map((item, index) => {
                                    return (
                                        <>
                                            <li
                                                style={{ textAlign: "left" }}
                                                key={`inv-${index}`}
                                            >
                                                {item.invoiceNumber} -{" "}
                                                <strong>Status:</strong>{" "}
                                                {item.status} -{" "}
                                                <strong>Reference Type:</strong>{" "}
                                                {item.referenceType}
                                            </li>
                                        </>
                                    );
                                })}
                            </ul>
                        )}
                        <Typography
                            variant="paragraph"
                            component="p"
                            color="secondary"
                        >
                            Please note, this action is not reversible.
                        </Typography>
                    </DialogContent>
                    <DialogActions style={{ justifyContent: "center" }}>
                        <Button
                            type="submit"
                            color="secondary"
                            variant="contained"
                            onClick={handleCancelCCNoteGeneration}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            variant="contained"
                            onClick={handleAcceptCCNoteGeneration}
                        >
                            Create Credit Note
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    );
}

export default KSA;
